'use client';

import { Suspense, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { PricingCards } from '@/components/pricing-cards';
import { AutoCheckout } from '@/components/pricing/auto-checkout';
import { PaygPacks } from '@/components/pricing/payg-packs';
import { Button } from '@/components/button';
import { Card, CardContent } from '@/components/card';
import { plans } from '@/lib/pricing-data';
import { useLocalizedLandingCopy } from '@/lib/marketing-copy-localized.client';
import { trackPricingCtaClicked } from '@/components/upgrade/track-paywall-cta';
import { cn } from '@/lib/utils';

interface PricingFAQ {
  question: string;
  answer: string;
}

const pricingFAQs: PricingFAQ[] = [
  {
    question: 'Can I switch plans anytime?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time from your dashboard. Upgrades take effect immediately with prorated charges. Downgrades take effect at the start of your next billing cycle.',
  },
  {
    question: 'What happens if I exceed my plan limits?',
    answer: 'We\'ll prompt you to upgrade or add minutes before you hit the limit. You can change your plan or buy extra minutes anytime. Free plan: overages are not allowed; you\'ll need to upgrade to continue. Unused minutes and characters do not roll over to the next month.',
  },
  {
    question: 'Do you offer refunds?',
    answer: 'We offer a 30-day money-back guarantee on all paid plans. If you\'re not satisfied with Wisprs, contact us within 30 days of your purchase for a full refund.',
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes. You can cancel your subscription at any time from your account settings. You keep access until the end of your current billing period. No cancellation fees apply.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept major credit and debit cards. Payments are processed securely through our billing provider. Every paid plan can be billed monthly or annually.',
  },
  {
    question: 'Can I try before I buy?',
    answer: 'Yes! The Free plan includes 30 minutes of transcription per day and 5,000 TTS characters per month. You can upgrade to a paid plan anytime to unlock more features and higher limits.',
  },
  {
    question: 'How does annual billing work?',
    answer: 'Every paid plan offers annual billing at 20% off the monthly rate: you pay for the year upfront (for example, Pro is $20/month billed annually instead of $25 month-to-month). Your subscription renews annually unless you cancel, and the 30-day money-back guarantee applies.',
  },
  {
    question: 'Can I upgrade or downgrade my plan?',
    answer: 'Yes. Change your plan anytime from your dashboard or billing settings. Upgrades apply immediately with proration; downgrades apply at the start of the next billing cycle.',
  },
];

function PricingPageContent() {
  const searchParams = useSearchParams();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const pricingCopy = useLocalizedLandingCopy('pricing');

  // NOTE: this used to redirect signed-in visitors with ?plan= to
  // /dashboard/pricing — which then discarded the plan param and made them pick
  // again. That round trip was the funnel's dead end. /pricing is now the single
  // pricing surface for signed-out and signed-in users alike; <AutoCheckout />
  // resumes the purchase instead of bouncing.

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden w-full">
      <Navigation />
      
      <main className="flex-1 w-full overflow-x-hidden">
        {/* Pricing Section */}
        <section className="py-24 md:py-32 border-b border-[rgb(var(--surface-border))] bg-[rgb(var(--background))]" id="plans">
          <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold text-[rgb(var(--foreground))] mb-4">
                {pricingCopy.hero.heading}
              </h1>
              <p className="text-lg text-[rgb(var(--foreground-secondary))] max-w-2xl mx-auto mb-8">
                {pricingCopy.hero.subhead}
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                <Link
                  href={pricingCopy.hero.primaryCta.href}
                  className="w-full sm:w-auto"
                  onClick={() =>
                    trackPricingCtaClicked({
                      cta: 'hero_primary',
                      from: searchParams.get('from') ?? undefined,
                      page: '/pricing',
                    })
                  }
                >
                  <Button variant="primary" size="lg" className="w-full sm:w-auto min-h-[44px]">
                    {pricingCopy.hero.primaryCta.label}
                  </Button>
                </Link>
                {pricingCopy.hero.secondaryCta && (
                  <Link
                    href={pricingCopy.hero.secondaryCta.href}
                    className="w-full sm:w-auto"
                    onClick={() =>
                      trackPricingCtaClicked({
                        cta: 'hero_secondary',
                        from: searchParams.get('from') ?? undefined,
                        page: '/pricing',
                      })
                    }
                  >
                    <Button variant="secondary" size="lg" className="w-full sm:w-auto min-h-[44px]">
                      {pricingCopy.hero.secondaryCta.label}
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            <AutoCheckout />

            {/* The free card is shown deliberately. Rendering paid plans only left
                three near-identical cards with no anchor and no entry point — 274
                visitors in 14 days clicked none of them. */}
            <PricingCards plans={plans} />

            <PaygPacks />
          </div>
        </section>

        {/* Pricing/Subscription FAQ Section */}
        <section className="py-24 md:py-32 border-b border-[rgb(var(--surface-border))] bg-[rgb(var(--background))]">
          <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[rgb(var(--foreground))] mb-4">
                Transcription pricing, plans, and billing
              </h2>
              <p className="text-lg text-[rgb(var(--foreground-secondary))] max-w-2xl mx-auto">
                Speech to text plans: free tier, upgrades, and limits
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto min-w-0">
              {/* Left Column */}
              <Card variant="elevated" className="p-6">
                <CardContent className="p-0 space-y-4">
                  {pricingFAQs.slice(0, 4).map((faq, index) => {
                    const isOpen = openIndex === index;

                    return (
                      <div
                        key={index}
                        className="border-b border-[rgb(var(--surface-border))] last:border-0 pb-4 last:pb-0"
                      >
                        <button
                          type="button"
                          onClick={() => toggleAccordion(index)}
                          className="w-full flex items-center justify-between gap-4 text-left group min-h-[44px] py-2 -my-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--primary))] focus-visible:ring-offset-2"
                          aria-expanded={openIndex === index}
                        >
                          <h3 className="text-base font-semibold text-[rgb(var(--foreground))] group-hover:text-[rgb(var(--primary))] transition-colors pr-8">
                            {faq.question}
                          </h3>
                          <svg
                            className={cn(
                              'w-5 h-5 text-[rgb(var(--foreground-secondary))] flex-shrink-0 transition-transform duration-300',
                              isOpen && 'rotate-180'
                            )}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        <div
                          className={cn(
                            'overflow-hidden transition-all duration-300 ease-in-out',
                            isOpen ? 'max-h-96 mt-3' : 'max-h-0'
                          )}
                        >
                          <p className="text-sm text-[rgb(var(--foreground-secondary))] leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* Right Column */}
              <Card variant="elevated" className="p-6">
                <CardContent className="p-0 space-y-4">
                  {pricingFAQs.slice(4, 8).map((faq, index) => {
                    const actualIndex = index + 4; // Offset by 4 for right column
                    const isOpen = openIndex === actualIndex;

                    return (
                      <div
                        key={actualIndex}
                        className="border-b border-[rgb(var(--surface-border))] last:border-0 pb-4 last:pb-0"
                      >
                        <button
                          type="button"
                          onClick={() => toggleAccordion(actualIndex)}
                          className="w-full flex items-center justify-between gap-4 text-left group min-h-[44px] py-2 -my-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--primary))] focus-visible:ring-offset-2"
                          aria-expanded={openIndex === actualIndex}
                        >
                          <h3 className="text-base font-semibold text-[rgb(var(--foreground))] group-hover:text-[rgb(var(--primary))] transition-colors pr-8">
                            {faq.question}
                          </h3>
                          <svg
                            className={cn(
                              'w-5 h-5 text-[rgb(var(--foreground-secondary))] flex-shrink-0 transition-transform duration-300',
                              isOpen && 'rotate-180'
                            )}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        <div
                          className={cn(
                            'overflow-hidden transition-all duration-300 ease-in-out',
                            isOpen ? 'max-h-96 mt-3' : 'max-h-0'
                          )}
                        >
                          <p className="text-sm text-[rgb(var(--foreground-secondary))] leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Anyone who read eight FAQ answers is deciding. Previously the page
                simply ended here, with the only CTAs three screens back up. */}
            <div className="mt-16 text-center">
              <p className="text-lg text-[rgb(var(--foreground-secondary))] mb-6">
                Still deciding? Start on the free plan. No card required.
              </p>
              <Link
                href="/sign-up"
                onClick={() =>
                  trackPricingCtaClicked({
                    cta: 'hero_primary',
                    from: 'faq_footer',
                    page: '/pricing',
                  })
                }
              >
                <Button variant="primary" size="lg" className="min-h-[44px]">
                  Start transcribing free
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default function PricingPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex flex-col overflow-x-hidden w-full">
        <Navigation />
        <main className="flex-1 w-full overflow-x-hidden flex items-center justify-center min-h-[50vh]">
          <p className="text-[rgb(var(--foreground-secondary))]">Loading…</p>
        </main>
        <Footer />
      </div>
    }>
      <PricingPageContent />
    </Suspense>
  );
}

